import React, { useEffect, useRef, useState, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./App.css";
import "./Homepage.css";
import "./pollutantPage.css";
import audioService from "./AudioService";
import { ScrollToTop, forceScrollToTop } from './ScrollFix';
import { fetchSoundData, fetchPollutantData } from './Data/parseSheetData';
import { useMediaQuery } from "react-responsive";
// import { Analytics } from "@vercel/analytics/react"

import IsolatedCursor from "./IsolatedCursor";
import AudioEnablePopup from "./AudioPopup";
import Homepage from "./Homepage";

// Helper to gracefully retry loading chunk if a deployment/cache mismatch occurs
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem("chunk_retry_refreshed") || "false"
    );

    try {
      const component = await componentImport();
      window.sessionStorage.setItem("chunk_retry_refreshed", "false");
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.sessionStorage.setItem("chunk_retry_refreshed", "true");
        window.location.reload();
        return;
      }
      throw error;
    }
  });

const PollutantPage = lazyWithRetry(() => import("./PollutantPage"));
const PlayPads = lazyWithRetry(() => import("./PlayPads"));


// Analytics configuration mapping
const ANALYTICS_CONFIG = {
  // Homepage
  homepage: "G-8T2J98QBVF",

  // Pollutant-specific measurement IDs
  pollutants: {
    "potassium": "G-CS2Z7R1Z7T", //Done
    "simazine": "G-3FXNHT8R7S",  //Done
    "imidacloprid": "G-RVC6EJZML5",  //Done
    "atrazine": "G-WXESRQ4JJR",  //Done
    "glyphosate": "G-C4XFTK6RJ2",  //Done
    "phosphorus": "G-G653J290S3",  //Done
    "nitrates": "G-KEFRVDVFP6",  //Done
    "dimethomorph": "G-FQVTMMWMHF",  //Done
    "mercury": "G-78R22FYCSZ",  //Done
    "copper": "G-HLGK68JPZQ",  //Done
    "lead": "G-6B1NDK82Q3",  //Done
    "chromium": "G-VM4ND8H0N8",  //Done
    "cadmium": "G-3BQJR55ZJL",  //Done
    "thallium": "G-QQQ0WKP4XQ",  //Done
    "selenium": "G-GE9MV451RP", //Done
    "nickel": "G-NNXFECWPV1",  //Done
    "arsenic": "G-P8H8CLBP6N", //Done
    "zinc": "G-ZTESVRJB69",  //Done
    "iron": "G-TTV9W4JVGT",  //Done
    "manganese": "G-PNE11XHVG9",  //Done
    "aluminium": "G-D4LQ93KC6B",  //Done
    "antimony": "G-SH9ZBY3SV1",  //Done
    "thorium": "G-E695PBKH7G",  //Done
    "strontium": "G-D3G0E8E3LZ",  //Done
    "uranium": "G-H6KXX6BC05",  //Done
    "cesium": "G-R130QFL12R",  //Done
    "polyaromatichydrocarbon": "G-ZMDR83D42F",  //Done
    "crudeoil": "G-2ZQ07HE3MC",  //Done
    "benzene": "G-BQFYEJ0DCT",
    "petrol": "G-LF10V9BZW7",
    "sulphide": "G-KZY08T7QEH",  //Done
    "diesel": "G-84L74GVHRX",  //Done
    "ammonium": "G-REEF8EJZHF",  //Done
    "phenol": "G-VPNNPJSC7F",  //Done
    "organicmatter": "G-M423YNEJCL",  //Done
    "estradiol": "G-JK75D40PCK",  //Done
    "phthalate": "G-8BY80D7YLR",  //Done
    "fragrance": "G-TQMM1Z1652",  //Done
    "diclofenac": "G-KVEXY8PQ0G",  //Done
    "bht": "G-NDBMD8SPM8",  //Done
    "syntheticdyes": "G-ZPXLM0QVTZ",   //Done
    "ibuprofen": "G-JC5T6W54X6",  //Done
    "ofloxacinantibiotic": "G-YF5KRMN9B7",  //Done
    "chlorides": "G-N73Y7NZDRZ",  //Done
    "tetracyclineAntibiotic": "G-GHQZGKTZLK",  //Done
    "wastewatersludge": "G-81WQH91J48"  //Done
  },

  // Default fallback
  default: "G-8T2J98QBVF"
};


const AppContent = () => {
  const location = useLocation();
  const [dataByCategory, setDataByCategory] = useState({});
  const [pageLoaded, setPageLoaded] = useState(false);
  const [displayHomePage, setDisplayHomePage] = useState(true);
  const [routeTransitionActive, setRouteTransitionActive] = useState(false);
  const [pollutantPageReady, setPollutantPageReady] = useState(false);
  const [poemImageUrl, setPoemImageUrl] = useState("");
  const [TitleImage, setTitleImage] = useState("");
  // Tracks whether ANY loading screen is currently covering the page
  const [isLoadingScreen, setIsLoadingScreen] = useState(false);
  const [showAudioPopup, setShowAudioPopup] = useState(false);
  const initialMountRef = useRef(true);
  const previousPathRef = useRef('');
  const isMobile = useMediaQuery({ maxWidth: 820 });

  useEffect(() => {
    if (isMobile) {
      // Always show on mobile
      setShowAudioPopup(true);
    } else {
      // Desktop: show only if no consent
      const consent = localStorage.getItem("audioConsentGranted");
      if (consent !== "true") {
        setShowAudioPopup(true);
      }
    }
  }, [isMobile]);

  const handleAudioPopupClick = async () => {
    // Save consent only on desktop
    if (!isMobile) {
      localStorage.setItem("audioConsentGranted", "true");
    }

    setShowAudioPopup(false);

    // Resume AudioContext to unlock audio playback
    audioService.setMuted(false);

    if (audioService.audioContext) {
      try {
        await audioService.audioContext.resume();
        console.log("[App] AudioContext successfully resumed via popup click");
      } catch (err) {
        console.warn("[App] Failed to resume AudioContext via popup click:", err);
      }
    }

    try {
      // Only start ambient sound if we are on the homepage
      if (location.pathname === "/") {
        await audioService.playAmbientSound(1.0, 1500);
        console.log("[App] Ambient sound started via popup click");
      } else {
        console.log(
          "[App] Popup clicked on non-homepage, letting SoundToggle play the page sound"
        );
      }
    } catch (e) {
      console.error("[App] Failed to start ambient sound via popup click:", e);
    }
  };

  // 🔹 Enhanced tracking with multiple GA measurement IDs
  useEffect(() => {
    if (!window.gtag) return;

    const eventPayload = {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname + location.search,
    };

    if (location.pathname === "/") {
      window.gtag("config", ANALYTICS_CONFIG.homepage, eventPayload);
      Object.values(ANALYTICS_CONFIG.pollutants).forEach((id) => {
        window.gtag("config", id, eventPayload);
      });
    } else if (location.pathname === "/playtest") {
      window.gtag("config", ANALYTICS_CONFIG.homepage, eventPayload);
      window.gtag("event", "page_view", eventPayload);
    } else {
      const pollutantName = location.pathname.substring(1);
      const measurementId = ANALYTICS_CONFIG.pollutants[pollutantName] || ANALYTICS_CONFIG.default;
      window.gtag("config", measurementId, eventPayload);
      window.gtag("event", "page_view", {
        ...eventPayload,
        measurement_id: measurementId,
      });
    }
  }, [location]);

  const navigate = useNavigate();

  useEffect(() => {
    const handleBeforeNavigate = () => {
      document.body.classList.add('lock-scroll');
    };
    window.addEventListener('beforeunload', handleBeforeNavigate);
    return () => window.removeEventListener('beforeunload', handleBeforeNavigate);
  }, []);

  useEffect(() => {
    const handleAnchorClick = (event) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const findAnchor = (target) => {
        if (!target) return null;

        if (event.composedPath) {
          for (const node of event.composedPath()) {
            if (node && node.nodeType === 1 && node.tagName?.toUpperCase() === 'A') {
              return node;
            }
          }
        }

        let element = target;
        while (element) {
          if (element.nodeType === 1 && element.tagName?.toUpperCase() === 'A') return element;
          element = element.parentNode;
        }
        return null;
      };

      const element = findAnchor(event.target);
      if (!element) return;

      const href = element.getAttribute('href') || element.getAttribute('xlink:href');
      if (!href) return;

      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (element.target && element.target !== '_self') return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash === window.location.hash) return;

      event.preventDefault();
      navigate(url.pathname + url.search + url.hash);
    };

    document.addEventListener('click', handleAnchorClick, true);
    return () => document.removeEventListener('click', handleAnchorClick, true);
  }, [navigate]);

  useEffect(() => {
    forceScrollToTop();

    if (location.pathname === "/") {
      document.body.classList.add("homepage-active");
    } else {
      document.body.classList.remove("homepage-active");
    }

    const timerId = setTimeout(() => {
      forceScrollToTop();
    }, 50);

    const timerId2 = setTimeout(() => {
      forceScrollToTop();
    }, 150);

    previousPathRef.current = location.pathname;
    return () => {
      clearTimeout(timerId);
      clearTimeout(timerId2);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/" && location.pathname !== "/playtest") {
      setPollutantPageReady(false);
    } else {
      setPollutantPageReady(false);
    }
  }, [location.pathname]);

  const handlePollutantPageReady = () => {
    setPollutantPageReady(true);
    // Clear the loading screen — SoundToggle will auto-start the pollutant sound
    audioService.setLoadingScreen(false);
    setIsLoadingScreen(false);

    // Ensure scroll position is reset to the very top when the pollutant page is revealed
    forceScrollToTop();
    requestAnimationFrame(() => {
      forceScrollToTop();
    });
    setTimeout(forceScrollToTop, 50);
    setTimeout(forceScrollToTop, 150);
    setTimeout(forceScrollToTop, 300);
  };

  // Keep AudioService's loading screen flag in sync with React state
  useEffect(() => {
    audioService.isLoadingScreen = isLoadingScreen;
  }, [isLoadingScreen]);

  useEffect(() => {
    if (location.pathname === "/") {
      // Do NOT set isLoadingScreen=true for homepage if it's already false or on mount
      // This allows audio to start playing as soon as possible
      const timer = setTimeout(() => {
        setDisplayHomePage(true);
        audioService.setLoadingScreen(false);
        setIsLoadingScreen(false);
      }, 50); // Minimal delay for layout stabilization
      return () => clearTimeout(timer);
    } else {
      // Going to a pollutant page — block audio while Loading Data.. is shown
      audioService.setLoadingScreen(true);
      setIsLoadingScreen(true);
    }
    return undefined;
  }, [location.pathname]);

  useEffect(() => {
    setRouteTransitionActive(false);
  }, [location.pathname]);

  const handleRouteTransitionStart = () => {
    setRouteTransitionActive(true);
    // Block and stop all audio immediately on nav click
    audioService.setLoadingScreen(true);
    setIsLoadingScreen(true);
    forceScrollToTop();
  };

  useEffect(() => {
    const fetchData = async () => {
      // ── Load data from local files in /public (no external API calls) ────────
      try {
        // Fetch both local data files in parallel
        const [soundRows, pollutantRows] = await Promise.all([
          fetchSoundData(),
          fetchPollutantData(),
        ]);
        console.log("Sound Data Rows:", soundRows);
        // ── Extract Title_Poem image URL from the sound sheet ─────────────────
        const poemRow = soundRows.find(row => row.Title_Poem);
        if (poemRow?.Title_Poem) {
          setPoemImageUrl(poemRow.Title_Poem);
        }

        const titleRow = soundRows.find(row => row.Title_Image);
        if (titleRow?.Title_Image) {
          setTitleImage(titleRow.Title_Image);
        }
        // ── Build primary mapping from the sound data ──────────────────────────
        audioService.init(soundRows);

        // ── Build cross-reference map from the pollutant data ──────────────────
        // The pollutant data has `id` (e.g. "potassium") and `Number` (pad num).
        // Only add entries that are NOT already in the mapping.
        const extraMap = {};
        pollutantRows.forEach(row => {
          const id = String(row.id || '').trim().toLowerCase();
          const pad = String(row.Number || '').trim();
          if (id && pad) {
            extraMap[id] = pad;
          }
        });
        audioService.addMappings(extraMap);

        // console.log('[App] Final AudioService mapping:', audioService.getMappingDebug());

        // ── Build categorised data for PollutantPage routing ──────────────────
        const categorizedData = pollutantRows.reduce((acc, row) => {
          const key = row.id || row.unique_id || row.Number;
          if (!acc[key]) acc[key] = [];
          acc[key].push(row);
          return acc;
        }, {});

        // console.log("categorizedData", categorizedData);
        setDataByCategory(categorizedData);
        setPageLoaded(true);
      } catch (err) {
        console.error("Error loading local data files:", err);
        setPageLoaded(true);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleLoad = () => {
      setPageLoaded(true);
    };

    if (document.readyState === "complete") {
      handleLoad();
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, []);

  useEffect(() => {
    return () => {
      audioService.dispose();
    };
  }, []);

  if (!pageLoaded) {
    return <div>Loading full page...</div>;
  }

  return (
    <div className="app-container">
      <ScrollToTop />
      <IsolatedCursor />
      <Navbar onNavigate={handleRouteTransitionStart} />
      {routeTransitionActive && (
        <div style={{ position: 'fixed', inset: 0, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: '#ffffff', zIndex: 20001 }}>
          <p>Loading Data ..</p>
        </div>
      )}
      <div className="page-container">
        <Suspense fallback={
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", width: "100%", backgroundColor: "#ffffff" }}>
            <p>Loading Data ..</p>
          </div>
        }>
          <Routes>
            <Route path="/" element={
              displayHomePage ? (
                <Homepage categorizedData={dataByCategory} poemImageUrl={poemImageUrl} TitleImage={TitleImage} />
              ) : (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", width: "100%", backgroundColor: "#ffffff" }}>
                  <p>Loading Data ..</p>
                </div>
              )
            } />
            <Route path="/:customName" element={
              <div style={{ position: "relative", width: "100%", height: "100%" }}>
                <PollutantPage
                  key={location.pathname}
                  onReady={handlePollutantPageReady}
                  categorizedData={dataByCategory}
                />
                {!pollutantPageReady && (
                  <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", width: "100%", backgroundColor: "#ffffff", zIndex: 20000 }}>
                    <p>Loading Data ..</p>
                  </div>
                )}
              </div>
            } />
            <Route path="/playtest" element={<PlayPads />} />
          </Routes>
        </Suspense>
      </div>
      <AudioEnablePopup visible={showAudioPopup} onClick={handleAudioPopupClick} />
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
      {/* <Analytics /> */}
    </Router>
  );
};

export default App;
